/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Ring buffer for per-atom velocity samples — the input to the
// vibrational-density-of-states (VDOS / phonon DOS) panel.
//
// Unlike the IR-spectrum buffer (which stores a single 3-vector per
// step — the charge-weighted velocity sum J = Σᵢqᵢvᵢ), VDOS requires
// the average of per-atom VACFs:
//
//   g(ω) ∝ FT⟨(1/N) Σᵢ vᵢ(0)·vᵢ(t)⟩
//
// This is NOT the same as the autocorrelation of the velocity sum —
// the latter mostly captures center-of-mass motion and misses optical
// modes. We therefore have to keep separate per-atom time series.
//
// Memory budget: tracking every atom in a 200-atom run for 25 000
// samples at 3 components × 4 bytes = 60 MB. Too much for a browser
// tab. Instead we cap the number of tracked atoms (default 16) and
// pick a representative subset spanning the unique element symbols.
// 16 × 25 000 × 12 = 4.8 MB plus 100 KB time stamps — comfortable.
//
// The tracked-atom set is locked in at `prepare()` time (when the user
// first starts recording) and stays fixed until the next `prepare()`
// or simulation reset. The DOS panel reports the tracked-atom list to
// the user so the sample is transparent.
//
// Like DipoleCurrentBuffer, the ring always holds one contiguous, uniformly
// sampled run — a push that doesn't continue the stride clears it first.

import { continuesSampleRun, sampleSpacingChanged } from './sampleContinuity';

interface AtomLike {
  elementSymbol: string;
}

export class VelocityBuffer {
  readonly capacity: number;
  readonly maxAtoms: number;
  // Per-atom Float32Arrays of length `capacity`. Lazily allocated in
  // prepare(); null before the first prepare() call.
  private vx: Float32Array[] | null = null;
  private vy: Float32Array[] | null = null;
  private vz: Float32Array[] | null = null;
  // Float64 stamps: Float32 loses sub-fs resolution past ~10⁷ fs (see DipoleCurrentBuffer).
  private t: Float64Array;
  private head = 0;
  private filled = false;
  // Stated sample spacing of the current run (null until a push says one).
  private runSpacingFs: number | null = null;
  // Indices into the source atoms array we are sampling. The integrator
  // hook iterates over these to fetch velocities each Nth step.
  trackedIndices: number[] = [];
  // Element symbol of each tracked atom, parallel to trackedIndices.
  // Surfaced in the panel ("tracked atoms: O, H, H, …") and useful for
  // the AI-analysis payload.
  trackedElements: string[] = [];
  // Layout of the atoms array prepare() picked from; see isPreparedFor().
  private layoutSignature = '';

  constructor(capacity: number, maxAtoms: number) {
    if (capacity < 2) throw new Error(`VelocityBuffer: capacity must be ≥ 2, got ${capacity}`);
    if (maxAtoms < 1) throw new Error(`VelocityBuffer: maxAtoms must be ≥ 1, got ${maxAtoms}`);
    this.capacity = capacity;
    this.maxAtoms = maxAtoms;
    this.t = new Float64Array(capacity);
  }

  // Choose the tracked-atom set and allocate per-atom storage. Picks
  // atoms in element-balanced round-robin order — for a system of
  // {N_O, N_H} atoms this would yield O, H, O, H, … rather than all
  // Os first, so even at low caps the sample covers every species.
  // Re-callable: discards any prior buffer state and re-picks.
  prepare(atoms: AtomLike[]): void {
    const indices = pickRepresentativeAtoms(atoms, this.maxAtoms);
    this.trackedIndices = indices;
    this.trackedElements = indices.map((i) => atoms[i].elementSymbol);
    this.layoutSignature = atomLayoutSignature(atoms);
    this.vx = indices.map(() => new Float32Array(this.capacity));
    this.vy = indices.map(() => new Float32Array(this.capacity));
    this.vz = indices.map(() => new Float32Array(this.capacity));
    this.head = 0;
    this.filled = false;
  }

  // Append one sample. `flatVelocities` carries the tracked atoms'
  // velocities in trackedIndices order, packed as
  // [vx0, vy0, vz0, vx1, vy1, vz1, …] — matches how the worker ships
  // velocities across postMessage as a single Float32Array. `spacingFs` is
  // the sampler's stated sample spacing (stride × timestep); a change starts
  // a new run — see sampleContinuity.
  push(flatVelocities: Float32Array, timeFs: number, spacingFs?: number): void {
    if (!this.vx || !this.vy || !this.vz) return;
    const n = this.trackedIndices.length;
    if (flatVelocities.length < n * 3) return;
    if (
      sampleSpacingChanged(this.runSpacingFs, spacingFs) ||
      !continuesSampleRun(this.t, this.head, this.filled, this.capacity, timeFs)
    )
      this.clear();
    if (spacingFs !== undefined) this.runSpacingFs = spacingFs;
    const h = this.head;
    for (let i = 0; i < n; i++) {
      this.vx[i][h] = flatVelocities[i * 3 + 0];
      this.vy[i][h] = flatVelocities[i * 3 + 1];
      this.vz[i][h] = flatVelocities[i * 3 + 2];
    }
    this.t[h] = timeFs;
    this.head = (h + 1) % this.capacity;
    if (this.head === 0) this.filled = true;
  }

  clear(): void {
    this.head = 0;
    this.filled = false;
    this.runSpacingFs = null;
  }

  isPrepared(): boolean {
    return this.vx !== null;
  }

  // Prepared against THIS atom layout? False once molecules were added,
  // removed or replaced since prepare(): trackedIndices would then point at
  // the wrong (or no longer existing) atoms, so the caller must re-prepare,
  // discarding samples that belong to the old layout.
  isPreparedFor(atoms: AtomLike[]): boolean {
    return this.isPrepared() && this.layoutSignature === atomLayoutSignature(atoms);
  }

  size(): number {
    return this.filled ? this.capacity : this.head;
  }

  isEmpty(): boolean {
    return this.size() === 0;
  }

  durationFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return this.t[last] - this.t[start];
  }

  averageDtFs(): number {
    const n = this.size();
    if (n < 2) return 0;
    return this.durationFs() / (n - 1);
  }

  // Return per-atom velocity time series in chronological order, ready
  // to feed into computeVdos. View into the backing storage when the
  // ring hasn't wrapped; freshly-allocated unfolded copies when it has.
  // Mirrors DipoleCurrentBuffer.getComponents — same view-or-copy
  // semantics so callers can treat the return as read-only.
  getPerAtomComponents():
    | {
        vx: Float32Array;
        vy: Float32Array;
        vz: Float32Array;
        element: string;
      }[]
    | null {
    const n = this.size();
    if (n < 2 || !this.vx || !this.vy || !this.vz) return null;
    return this.trackedIndices.map((_, atomIdx) => {
      let vx: Float32Array;
      let vy: Float32Array;
      let vz: Float32Array;
      if (!this.filled) {
        vx = this.vx![atomIdx].subarray(0, n);
        vy = this.vy![atomIdx].subarray(0, n);
        vz = this.vz![atomIdx].subarray(0, n);
      } else {
        vx = new Float32Array(n);
        vy = new Float32Array(n);
        vz = new Float32Array(n);
        const tail = this.head;
        const firstChunk = n - tail;
        vx.set(this.vx![atomIdx].subarray(tail), 0);
        vx.set(this.vx![atomIdx].subarray(0, tail), firstChunk);
        vy.set(this.vy![atomIdx].subarray(tail), 0);
        vy.set(this.vy![atomIdx].subarray(0, tail), firstChunk);
        vz.set(this.vz![atomIdx].subarray(tail), 0);
        vz.set(this.vz![atomIdx].subarray(0, tail), firstChunk);
      }
      return { vx, vy, vz, element: this.trackedElements[atomIdx] };
    });
  }

  // Time extent (fs) of the buffered window, for retrospective segment resolution.
  windowExtentFs(): { minFs: number; maxFs: number; count: number } {
    const n = this.size();
    if (n === 0) return { minFs: 0, maxFs: 0, count: 0 };
    const start = this.filled ? this.head : 0;
    const last = (this.head - 1 + this.capacity) % this.capacity;
    return { minFs: this.t[start], maxFs: this.t[last], count: n };
  }

  // Per-atom velocity series restricted to samples whose timestamp lies in
  // [t0Fs, t1Fs], plus the windowed averaged Δt and actual span. Null when fewer
  // than two samples fall inside. Feeds computeVdos over a trajectory SEGMENT.
  getPerAtomComponentsInWindow(
    t0Fs: number,
    t1Fs: number,
  ): {
    atoms: { vx: Float32Array; vy: Float32Array; vz: Float32Array; element: string }[];
    dtFs: number;
    startFs: number;
    endFs: number;
  } | null {
    const n = this.size();
    if (n < 2 || !this.vx || !this.vy || !this.vz) return null;
    const start = this.filled ? this.head : 0;
    const idxs: number[] = [];
    let startFs = Infinity;
    let endFs = -Infinity;
    for (let k = 0; k < n; k++) {
      const idx = (start + k) % this.capacity;
      const tk = this.t[idx];
      if (tk < t0Fs || tk > t1Fs) continue;
      idxs.push(idx);
      if (tk < startFs) startFs = tk;
      if (tk > endFs) endFs = tk;
    }
    if (idxs.length < 2) return null;
    const atoms = this.trackedIndices.map((_, a) => {
      const vx = new Float32Array(idxs.length);
      const vy = new Float32Array(idxs.length);
      const vz = new Float32Array(idxs.length);
      for (let j = 0; j < idxs.length; j++) {
        vx[j] = this.vx![a][idxs[j]];
        vy[j] = this.vy![a][idxs[j]];
        vz[j] = this.vz![a][idxs[j]];
      }
      return { vx, vy, vz, element: this.trackedElements[a] };
    });
    return { atoms, dtFs: (endFs - startFs) / (idxs.length - 1), startFs, endFs };
  }
}

// Cheap identity of an atom layout: count + element sequence. Any molecule
// add / remove / replace changes it (a same-composition swap does not, and the
// indices stay valid in that case). ~µs for the 500-atom cap.
export function atomLayoutSignature(atoms: AtomLike[]): string {
  let s = atoms.length + ':';
  for (let i = 0; i < atoms.length; i++) s += atoms[i].elementSymbol + ',';
  return s;
}

// Pick at most `maxCount` atom indices, balanced across distinct
// element symbols. Algorithm: bucket atoms by element, then round-
// robin one-at-a-time across buckets until the cap is reached.
// Guarantees that for any K ≤ maxCount, the first K picks are
// equally distributed across elements (modulo bucket exhaustion),
// so even at a cap of 4 a water simulation gets {O, H, O, H} rather
// than {O, O, O, O}.
export function pickRepresentativeAtoms(atoms: AtomLike[], maxCount: number): number[] {
  if (atoms.length === 0 || maxCount <= 0) return [];
  const buckets = new Map<string, number[]>();
  for (let i = 0; i < atoms.length; i++) {
    const sym = atoms[i].elementSymbol;
    let b = buckets.get(sym);
    if (!b) {
      b = [];
      buckets.set(sym, b);
    }
    b.push(i);
  }
  const out: number[] = [];
  // Stable element order — Map preserves insertion order, so atoms
  // are iterated in the order they first appeared.
  const symbols = Array.from(buckets.keys());
  let round = 0;
  while (out.length < maxCount) {
    let added = false;
    for (const sym of symbols) {
      if (out.length >= maxCount) break;
      const bucket = buckets.get(sym)!;
      if (round < bucket.length) {
        out.push(bucket[round]);
        added = true;
      }
    }
    if (!added) break; // every bucket exhausted
    round++;
  }
  return out;
}
